import React, { useContext, useState, useMemo } from 'react'
import { Pagination, Typography } from 'antd'
import BusinessDataSources from 'components/BusinessDataSources/BusinessDataSources'
import { Alert } from 'uikit/alerts/default/Alert'
import { normalizeUrlProtocol } from '../../utils'
import { WhiteBoxWrapper } from '../../WhiteBoxWrapper_new/WhiteBoxWrapper_new'
import { VerticalContext } from 'store/verticals/Context'
import styles from './businessesAndAddresses.module.scss'
import { uniqueByName } from './utils'

const ITEMS_PER_PAGE = 4
const { Text } = Typography

export const BusinessesAndAddresses = () => {
  const { verticalEntity } = useContext(VerticalContext)
  const [businessNamesPage, setBusinessNamesPage] = useState(1)
  const [addressesPage, setAddressesPage] = useState(1)

  const businessNames = useMemo(
    () => uniqueByName(verticalEntity?.overviewDetails?.businessNames || []),
    [verticalEntity?.overviewDetails?.businessNames],
  )

  const addresses = useMemo(() => {
    const allAddresses: Array<{ name: string; sources: Array<{ platform: string; uri?: string }> }> = []
    verticalEntity?.overviewDetails?.locations?.forEach(location => {
      location.businesses?.forEach(business => {
        if (business.address) {
          allAddresses.push({
            name: business.address,
            sources: business.sources || [],
          })
        }
      })
    })
    return uniqueByName(allAddresses)
  }, [verticalEntity?.overviewDetails?.locations])

  const paginatedBusinessNames = useMemo(() => {
    const startIndex = (businessNamesPage - 1) * ITEMS_PER_PAGE
    return businessNames.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [businessNames, businessNamesPage])

  const paginatedAddresses = useMemo(() => {
    const startIndex = (addressesPage - 1) * ITEMS_PER_PAGE
    return addresses.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [addresses, addressesPage])

  const handleBusinessNamesPageChange = (page: number) => {
    setBusinessNamesPage(page)
  }

  const handleAddressesPageChange = (page: number) => {
    setAddressesPage(page)
  }

  return (
    <WhiteBoxWrapper>
      <div className={styles.container}>
        <div>Raw Data Collected</div>

        <Alert type='info' title='Note:'>
          The following records show all names and addresses collected before registry matching. They are not directly
          linked to each other—the matching process analyzes these separately to identify official registry records.
          Names shown as collected from source data.
        </Alert>

        <div className={styles.cardsContainer}>
          <div className={styles.cardWrapper}>
            <p className={styles.cardTitle}>Collected Business Names / DBAs</p>
            <div className={styles.card}>
              <div className={styles.listContainer}>
                {paginatedBusinessNames.length > 0 ? (
                  paginatedBusinessNames.map((source, index) => (
                    <div key={`${source.name}-${index}`} className={styles.listItem}>
                      <Text ellipsis={{ tooltip: source.name }}>{source.name}</Text>
                      <div className={styles.sourcesContainer}>
                        <BusinessDataSources
                          dataSources={source.sources.map(dataSource => dataSource.platform)}
                          urls={source.sources.map(dataSource => dataSource.uri || '').filter(uri => !!uri)}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>No business names collected</div>
                )}
              </div>
              {businessNames.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    current={businessNamesPage}
                    pageSize={ITEMS_PER_PAGE}
                    total={businessNames.length}
                    onChange={handleBusinessNamesPageChange}
                    size='small'
                    showSizeChanger={false}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.cardWrapper}>
            <p className={styles.cardTitle}>Collected Addresses</p>
            <div className={styles.card}>
              <div className={styles.listContainer}>
                {paginatedAddresses.length > 0 ? (
                  paginatedAddresses.map((business, index) => (
                    <div key={`${business.name}-${index}`} className={styles.listItem}>
                      <Text ellipsis={{ tooltip: business.name }}>{business.name}</Text>
                      <div className={styles.sourcesContainer}>
                        <BusinessDataSources
                          dataSources={business.sources.map(dataSource => dataSource.platform)}
                          urls={business.sources.map(dataSource => normalizeUrlProtocol(dataSource?.uri || ''))}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>No addresses collected</div>
                )}
              </div>
              {addresses.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    current={addressesPage}
                    pageSize={ITEMS_PER_PAGE}
                    total={addresses.length}
                    onChange={handleAddressesPageChange}
                    size='small'
                    showSizeChanger={false}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </WhiteBoxWrapper>
  )
}
